import { stripDataForQuery } from "./dataTableBackendHelper";
import {
  AppConfigService,
  DataFilterService,
  RecordsetAdapterService,
  TableAdapterService,
  QueryClientService,
  QueryGeneratorService,
  QueryErrorInterpreterService,
  TableDataAdapterService,
  TableModel,
  FilterConfig,
  ApiResponse,
} from "./types";

export class DataTableQueryBackendService {
  constructor(
    private appConfig: AppConfigService,
    private dataFilter: DataFilterService,
    private recordsetAdapter: RecordsetAdapterService,
    private tableAdapter: TableAdapterService,
    private queryClient: QueryClientService,
    private queryGenerator: QueryGeneratorService,
    private queryErrorInterpreter: QueryErrorInterpreterService,
    private tableDataAdapter: TableDataAdapterService
  ) {}

  /**
   * Gets table metadata using query service
   * @param tableId - The table identifier
   * @param scope - Optional scope parameter
   * @returns Promise resolving to table model
   */
  getTableMetadata(tableId: string | number, scope?: string): Promise<TableModel> {
    return this.tableMetadataQuery(tableId, scope);
  }

  /**
   * Requests recordset data using query service
   * @param table - The table model
   * @param filterConfig - Filter configuration
   * @param globalFilters - Global filters array
   * @param scope - Optional scope parameter
   * @returns Promise resolving to recordset model
   */
  recordsetRequested(
    table: TableModel,
    filterConfig: FilterConfig,
    globalFilters: any[],
    scope?: string
  ): Promise<any> {
    const filterConfigObj = filterConfig.toJson();
    return this.tableQuery(table, filterConfigObj, globalFilters, scope).then(tableData => {
      const tableModel = this.tableAdapter.deserializeTable(tableData.metaData.id, tableData.metaData);
      return this.recordsetAdapter.deserializeRecordset(tableModel, filterConfig, tableData.result);
    });
  }

  /**
   * Private method to handle table metadata query
   * @param tableId - The table identifier
   * @param scope - Optional scope parameter
   * @returns Promise resolving to table model
   */
  private tableMetadataQuery(tableId: string | number, scope?: string): Promise<TableModel> {
    const query = this.queryGenerator.tableMetadata(tableId, scope);
    return new Promise((resolve, reject) => {
      this.queryClient
        .query(query)
        .then(response => {
          stripDataForQuery(response.data);
          const metadata = this.tableDataAdapter.formatMetadata(tableId, response.data);
          resolve(this.tableAdapter.deserializeTable(tableId, metadata));
        })
        .catch(response => {
          const errorCode = this.queryErrorInterpreter.errorCode(response);
          reject(errorCode);
        });
    });
  }

  /**
   * Private method to handle table data query
   * @param table - The table model
   * @param filterConfig - Filter configuration object
   * @param globalFilters - Global filters array
   * @param scope - Optional scope parameter
   * @returns Promise resolving to table data
   */
  private tableQuery(table: TableModel, filterConfig: any, globalFilters: any[], scope?: string): Promise<any> {
    const filterObj = this.dataFilter.getFilterRequestBody(filterConfig);
    const clearAllFilter = (window as any).clearAllFilter;

    const { filterList, sortField: sortObj } = filterObj;
    const positionField = table.systemFieldByName("metadata.position");
    const recordIdField = table.systemFieldByName("metadata.record_id");
    const defaultSortField = positionField ? positionField.name() : undefined;
    const defaultSortOrder = defaultSortField ? "asc" : undefined;
    const secondarySortField = recordIdField ? recordIdField.name() : undefined;
    const secondarySortOrder = secondarySortField ? "asc" : undefined;
    const startIndex = filterObj.params.start;

    const query = this.queryGenerator.table({
      tableId: table.id(),
      filterList,
      sortField: sortObj ? sortObj.field : defaultSortField,
      sortOrder: sortObj ? sortObj.order : defaultSortOrder,
      secondarySortField,
      secondarySortOrder,
      offset: startIndex - 1,
      globalFilters: clearAllFilter ? [] : globalFilters, // if clearfilterflag set to false, so global filters will be empty
      scope,
    });

    return new Promise((resolve, reject) => {
      this.queryClient
        .query(query)
        .then(response => {
          const tableData = this.tableDataAdapter.format(table.id(), response.data, startIndex);
          resolve(tableData);
        })
        .catch(response => {
          const errorCode = this.queryErrorInterpreter.errorCode(response);
          reject(errorCode);
        });
    });
  }
}
